/** @prettier */
import classNames from 'classnames';
import { isAfter } from 'date-fns';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { withRouter } from 'react-router-dom';

// import PwrBy from '@xanadu/ui-shared/components/PwrBy';
import {
  ALDERNEY_URL,
  GAMBLING_CONTROL,
  GAME_CARE_URL,
  GAMSTOP_URL,
  PLUS18_URL,
  TTTT_URL,
} from '@xanadu/ui-shared/constants';
import { useReduxBridge } from '@xanadu/ui-shared/contexts/ReduxBridgeContext';
import { useImprovedTranslation } from '@xanadu/ui-shared/hooks';
import createMarkup from '@xanadu/ui-shared/utils/createMarkupHTML';

import { useIsPreLaunch } from '../../hooks';

import { getClientFamilyIfAny, getClientLogo } from '../../utils';

import Logo18Plus from '../../../../images/footer/Logo_18Plus_Light.svg';
import LogoAlderney from '../../../../images/footer/Logo_Alderney_Light.svg';
import LogoGamCare from '../../../../images/footer/Logo_GamCare_Light.svg';
import LogoGamblingCommission from '../../../../images/footer/Logo_GamblingCommission_Light.svg';
import LogoGamstop from '../../../../images/footer/Logo_Gamstop_Light.svg';
import LogoTimeToThink from '../../../../images/footer/Logo_TimeToThink_Light.svg';

import Placeholder from './Placeholder';
import styles from './PreLaunch.module.scss';

const PreLaunch = ({
  isAppPreLaunch,
  androidStoreLink,
  iosStoreLink,
  history: routerHistory,
  preLaunchText,
  underageWarning,
  fallbackReleaseDate,
  image,
}) => {
  const [pollingText, setPollingText] = useState(null);
  const [pollingWarning, setPollingWarning] = useState(null);
  const [pollingImage, setPollingImage] = useState(null);
  const [typedSequence, setTypedSequence] = useState('');
  const imageRef = useRef();
  const leftSectionRef = useRef();
  const cardRef = useRef();
  const logosRef = useRef();
  const intervalRef = useRef();
  const { clientName, clientSite } = useReduxBridge() || {};
  const {
    i18n: { language: activeLanguage },
  } = useImprovedTranslation();

  const mainClass = classNames({
    [styles.main]: true,
  });

  const urlSegments = routerHistory.location.pathname.split('/');

  const familyElement = getClientFamilyIfAny(clientName);
  const ClientLogo = getClientLogo(clientName, clientSite);

  // Temporarily remove bypass button
  const handleBypass = () => {
    try {
      localStorage.setItem('prelaunch-bypass', 'true');

      if (cardRef.current) {
        cardRef.current.classList.add(styles['card--bypass-exit']);
      }

      setTimeout(() => {
        if (logosRef.current) {
          logosRef.current.classList.add(styles['logos--bypass-exit']);
        }
      }, 300);

      setTimeout(() => {
        if (leftSectionRef.current) {
          leftSectionRef.current.classList.add(styles['section--bypass-collapse']);
        }
        if (imageRef.current) {
          imageRef.current.classList.add(styles['section--bypass-expand']);
        }
      }, 400);

      setTimeout(() => {
        window.location.replace('/');
      }, 900);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('Error setting bypass flag in localStorage');
    }
  };

  useEffect(() => {
    const handleKeyPress = (event) => {
      const newSequence = (typedSequence + event.key).slice(-7).toLowerCase();
      setTypedSequence(newSequence);

      if (newSequence === 'revelio') {
        handleBypass();
      }
    };

    window.addEventListener('keypress', handleKeyPress);
    return () => {
      window.removeEventListener('keypress', handleKeyPress);
    };
  }, [typedSequence]);

  useEffect(() => {
    if (urlSegments[urlSegments.length - 1] === 'bpass') {
      handleBypass();
    }
  }, [routerHistory.location.pathname]);

  useEffect(() => {
    let timer1;
    let timer2;
    let timer3;

    document.body.classList.add('preLaunch');

    if (imageRef.current && leftSectionRef.current && cardRef.current && logosRef.current) {
      timer1 = setTimeout(() => {
        imageRef.current.classList.add(styles['section--animate']);
        leftSectionRef.current.classList.add(styles['section--animate']);

        timer2 = setTimeout(() => {
          cardRef.current.classList.add(styles['card--animate']);

          timer3 = setTimeout(() => {
            logosRef.current.classList.add(styles['logos--animate']);
          }, 1200);
        }, 240);
      }, 240);
    }

    try {
      const storedLaunchDate = localStorage.getItem('launch-date');
      const bypassEnabled = localStorage.getItem('prelaunch-bypass');

      if (bypassEnabled || (storedLaunchDate && isAfter(new Date(), new Date(storedLaunchDate)))) {
        routerHistory.replace({
          pathname: '/',
        });
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('Error accessing localStorage');
    }

    intervalRef.current = setInterval(() => {
      useIsPreLaunch({
        site: clientSite,
        brand: clientName,
        path: routerHistory.location.pathname,
        polling: true,
        fallbackReleaseDate,
      }).then(
        ({
          isPreLaunch,
          preLaunchText: text,
          underageWarning: warning,
          preLaunchImage: newImage,
        }) => {
          setPollingText(text);
          setPollingWarning(warning);
          setPollingImage(newImage);
          if (!isPreLaunch) {
            setTimeout(() => {
              window.location.reload();
            }, 5 * 1000);
          }
        }
      );
    }, 120 * 1000);

    if (!preLaunchText) {
      // if we have the local storage item we need to acquire the text
      useIsPreLaunch({
        site: clientSite,
        brand: clientName,
        path: routerHistory.location.pathname,
        polling: true,
        fallbackReleaseDate,
      }).then(({ preLaunchText: text, underageWarning: warning, preLaunchImage: newImage }) => {
        setPollingText(text);
        setPollingWarning(warning);
        setPollingImage(newImage);
      });
    }

    return () => {
      document.body.classList.remove('preLaunch');

      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);

      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = setInterval(() => {
        useIsPreLaunch({
          site: clientSite,
          brand: clientName,
          path: routerHistory.location.pathname,
          polling: true,
          fallbackReleaseDate,
        }).then(
          ({
            isPreLaunch,
            preLaunchText: text,
            underageWarning: warning,
            preLaunchImage: newImage,
          }) => {
            setPollingText(text);
            setPollingWarning(warning);
            setPollingImage(newImage);
            if (!isPreLaunch) {
              setTimeout(() => {
                window.location.reload();
              }, 5 * 1000);
            }
          }
        );
      }, 120 * 1000);
    }
  }, [activeLanguage]); // we want fetchService to pick up the change

  const dangerousText = useMemo(
    () => createMarkup(pollingText || preLaunchText),
    [pollingText, preLaunchText]
  );

  const dangerousWarning = useMemo(
    () => createMarkup(pollingWarning || underageWarning),
    [pollingWarning, underageWarning]
  );

  const memoizedImageStyle = useMemo(() => {
    if (image || pollingImage) {
      return `url(/dA/${pollingImage || image}) center 0 no-repeat`;
    }
    return 'url(/assets/images/pre-launch/orangefans-1.jpg) center 0 no-repeat';
  }, [image, pollingImage]);

  const imageStyle = {
    background: `${memoizedImageStyle}`,
    backgroundSize: 'cover',
  };

  return (
    <div className={mainClass}>
      <div className={styles.container}>
        <div className={styles.section} ref={leftSectionRef}>
          <div className={styles.card} ref={cardRef}>
            <div>
              <div className={styles.title}>
                <ClientLogo />
              </div>
              {!preLaunchText && !pollingText ? <Placeholder /> : null}
              {<div dangerouslySetInnerHTML={dangerousText} />}
              {!isAppPreLaunch && (iosStoreLink || androidStoreLink) && (
                <div className={styles.appLinks}>
                  {Boolean(androidStoreLink) && (
                    <a href={androidStoreLink} className={styles.androidLink}>
                      <img
                        src="/assets/images/native-apps-popup/PlayStoreBadge.png"
                        alt="Android App Store app download link"
                      />
                    </a>
                  )}
                  {Boolean(iosStoreLink) && (
                    <a href={iosStoreLink} className={styles.iosLink}>
                      <img
                        src="/assets/images/native-apps-popup/AppStoreBadge.png"
                        alt="iOS App Store app download link"
                      />
                    </a>
                  )}
                </div>
              )}
              <div
                className={styles.gambleResponsibly}
                dangerouslySetInnerHTML={dangerousWarning}
              />
            </div>
            <div className={styles.footer}>
              {/* Remove temporarily */}
              {/* <PwrBy className={styles.poweredBy} hyperlinked /> */}
              <div className={styles.familyElement}>{familyElement}</div>
            </div>
          </div>
          <div className={styles.familyElement}>{familyElement}</div>
          <div className={styles.logos} ref={logosRef}>
            <div>
              <a href={GAMBLING_CONTROL} target="_blank" rel="noopener noreferrer">
                <LogoGamblingCommission />
              </a>
              <a href={ALDERNEY_URL} target="_blank" rel="noopener noreferrer">
                <LogoAlderney />
              </a>
            </div>
            <div>
              <a href={TTTT_URL} target="_blank" rel="noopener noreferrer">
                <LogoTimeToThink />
              </a>
              <a href={GAME_CARE_URL} target="_blank" rel="noopener noreferrer">
                <LogoGamCare />
              </a>
            </div>
            <div>
              <a href={PLUS18_URL} target="_blank" rel="noopener noreferrer">
                <Logo18Plus />
              </a>
              <a
                className={styles.gamstopLogo}
                href={GAMSTOP_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                <LogoGamstop />
              </a>
            </div>
          </div>
          {/* Remove temporarily */}
          {/* <PwrBy className={styles.poweredBy} hyperlinked /> */}
        </div>
        <div className={styles.section} ref={imageRef} style={imageStyle} />
      </div>
    </div>
  );
};

PreLaunch.propTypes = {
  isAppPreLaunch: PropTypes.bool.isRequired,
  androidStoreLink: PropTypes.string,
  iosStoreLink: PropTypes.string,
  history: PropTypes.shape({
    replace: PropTypes.func.isRequired,
  }),
  preLaunchText: PropTypes.string,
  underageWarning: PropTypes.string,
  fallbackReleaseDate: PropTypes.instanceOf(Date).isRequired,
  image: PropTypes.string,
};

export default withRouter(PreLaunch);
